import React, {useEffect, useRef, useState} from "react";
import {Controller, useFormState} from "react-hook-form";
import InputMask from "react-input-mask";
import TextField from "@mui/material/TextField";
import {InputAdornment, Popper} from "@mui/material";
import {getNestedError} from "utils";
import {Svg} from "assets";

const hours = Array.from({length: 24}, (_, i) => String(i).padStart(2, "0"));
const minutes = Array.from({length: 60}, (_, i) => String(i).padStart(2, "0"));

export const TimeInput = ({
                              customDesign = true,
                              label,
                              labelHeader,
                              placeholder,
                              control,
                              rules,
                              name,
                              className,
                              disabled,
                              onBlur,
                          }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const inputRef = useRef(null);
    const prevHasError = useRef(false);
    const [isDisplayedValue, setIsDisplayedValue] = useState(false);

    const {errors} = useFormState({control});
    const error = getNestedError(errors, name);
    const hasError = !!error?.type;

    const open = Boolean(anchorEl);
    const id = open ? "simple-popper" : undefined;

    useEffect(() => {
        if (hasError && !prevHasError.current && inputRef.current) {
            inputRef.current.scrollIntoView({behavior: "smooth", block: "center"});
        }
        prevHasError.current = hasError;
    }, [hasError]);

    const handleClick = (event) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const to12Hour = (value24) => {
        if (!value24) return "";
        const [h, m] = value24.split(":").map(Number);
        if (isNaN(h) || isNaN(m)) return "";
        const ampm = h >= 12 ? "PM" : "AM";
        const hour12 = h % 12 || 12;
        return `${String(hour12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
    };

    const sanitizeTime = (input) => {
        if (!input) return "";
        let ampm = "";
        input = input.toUpperCase().trim();

        if (input.endsWith("AM") || input.endsWith("PM")) {
            ampm = input.slice(-2);
            input = input.slice(0, -2).trim();
        }

        let [rawH = "", rawM = ""] = input.split(":");
        rawH = rawH.replace(/\D/g, "");
        rawM = rawM.replace(/\D/g, "");

        let h = rawH;
        if (rawH.length > 0) {
            let numH = Number(rawH);
            if (ampm === "PM" && numH < 12) numH += 12;
            if (ampm === "AM" && numH === 12) numH = 0;
            if (numH > 23) numH = 23;
            h = String(numH).padStart(2, "0");
        }

        let m = rawM;
        if (rawM.length > 0) {
            let numM = Number(rawM);
            if (numM > 59) numM = 59;
            m = String(numM).padStart(2, "0");
        }

        if (rawH && rawM) return `${h}:${m}`;
        if (rawH) return `${h}${input.includes(":") ? ":" : ""}`;
        return "";
    };

    const handleSelect = (type, valueItem, value, onChange) => {
        let [h, m] = value?.split(":") || ["", ""];
        if (type === "hour") h = valueItem;
        if (type === "minute") m = valueItem;
        const newValue = `${h}:${m}`;
        onChange(newValue);
    };

    return (
        <div
            className={`input-main-wrapper ${customDesign ? "input-custom-wrapper" : ""} ${
                className ? className : ""
            }`}
        >
            <Controller
                control={control}
                name={name}
                rules={rules}
                render={({field: {onChange, value, name, ref}}) => (
                    <>
                        {labelHeader && (
                            <p className={`input-main-label-header ${error?.message ? "errorLabel" : ""}`}>
                                {labelHeader}
                                {rules?.required && <span className="input-main-label-header-required">*</span>}
                            </p>
                        )}

                        <InputMask
                            mask={isDisplayedValue ? "**:**" : "**:** **"}
                            maskChar={null}
                            value={isDisplayedValue ? value : to12Hour(value)}
                            disabled={disabled}
                            onFocus={() => setIsDisplayedValue(true)}
                            onBlur={(e) => {
                                const cleaned = sanitizeTime(e.target.value);
                                onChange(cleaned);
                                setIsDisplayedValue(false)
                                if (onBlur) onBlur(e);
                            }}
                            onChange={(e) => {
                                const cleaned = sanitizeTime(e.target.value);
                                onChange(cleaned);
                            }}
                        >
                            {() => (
                                <TextField
                                    error={!!error?.message}
                                    name={name}
                                    inputRef={(el) => {
                                        ref(el);
                                        inputRef.current = el;
                                    }}
                                    autoComplete="off"
                                    placeholder={placeholder}
                                    label={label}
                                    disabled={disabled}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" onClick={handleClick}>
                                                <Svg.AccessTimeSvg/>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}
                        </InputMask>

                        <Popper
                            id={id}
                            open={open}
                            anchorEl={anchorEl}
                            className="custom-select-time-box"
                            placement="bottom-start"
                            style={{position: "absolute", zIndex: 2600}}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div
                                className="hour-minute-box"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div>
                                    {hours.map((item) => (
                                        <button
                                            key={`hour-${item}`}
                                            onClick={() => handleSelect("hour", item, value, onChange)}
                                            type="button"
                                        >
                                            {item}
                                        </button>
                                    ))}
                                </div>
                                <div>
                                    {minutes.map((item) => (
                                        <button
                                            key={`minute-${item}`}
                                            onClick={() => handleSelect("minute", item, value, onChange)}
                                            type="button"
                                        >
                                            {item}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </Popper>
                    </>
                )}
            />

            {open && <div className="beck-drop" onClick={() => setAnchorEl(null)}/>}
        </div>
    );
};
